import { ReactNode } from 'react';
import { MAIN_PACE_TASK_IDs } from '@symfa-inc/providence-verizon-types';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import {
  priceCommaReplacer,
  projectFuzeIDRender,
  projectPaceTaskDataRender,
  providenceAgentRender,
  readOnlyDatePickerRenderer,
  readOnlyInputNumberRender,
} from '@core/utils/methods';
import { actionButtons } from '@models/constants';
import {
  AccountingBrowse,
  IdAndValue,
  OOMInformation,
  SiteBrowse,
} from '@models/interfaces';
import { Browse, ColumnsFunc } from '@models/types';

export const getAccountingColumns = (
  editFunc?: ColumnsFunc<AccountingBrowse>,
): ColumnsType<Browse> => [
  {
    title: 'Fuze ID',
    dataIndex: 'fuzeIDs',
    key: 'fuzeId',
    width: 150,
    sorter: true,
    render: projectFuzeIDRender({
      field: 'fuzeID',
    }),
  },
  {
    title: 'PS Project Number',
    dataIndex: 'fuzeIDs',
    key: 'PSProjectNumber',
    width: 150,
    sorter: true,
    render: projectFuzeIDRender({
      field: 'PSProjectNumber',
    }),
  },
  {
    title: 'PS Location Code',
    dataIndex: 'site',
    key: 'PSLocationCode',
    width: 130,
    sorter: true,
    render: (site: SiteBrowse): ReactNode =>
      readOnlyInputNumberRender(site?.PSLocationCode),
  },
  {
    title: 'Site Name',
    dataIndex: 'site',
    key: 'siteName',
    width: 140,
    sorter: true,
    render: (site: SiteBrowse): ReactNode => site?.siteName,
  },
  {
    title: 'Contract Number',
    width: 150,
    key: 'contractNumber',
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.contractNumber,
  },
  {
    title: 'Alternate Site Info',
    width: 170,
    key: 'alternateSiteInfo',
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.alternateSiteInfo,
  },
  {
    title: 'Submarket',
    key: 'submarket',
    width: 140,
    sorter: true,
    dataIndex: 'site',
    render: (site: SiteBrowse): ReactNode => site.submarket?.value,
  },
  {
    title: 'Providence Agent',
    key: 'providenceAgent',
    width: 180,
    sorter: true,
    dataIndex: 'providenceAgent',
    render: providenceAgentRender,
  },
  {
    title: 'Tower Owner',
    key: 'towerOwner',
    width: 145,
    dataIndex: 'site',
    sorter: true,
    render: (site: SiteBrowse): ReactNode => site?.towerOwner?.value,
  },
  {
    title: 'Project Type',
    dataIndex: 'projectType',
    key: 'projectType',
    width: 140,
    sorter: true,
    render: (projectType: IdAndValue): ReactNode => projectType?.value,
  },
  {
    title: 'SS010 Actual',
    key: 'SS010',
    dataIndex: 'projectPaceTaskData',
    width: 140,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.candidateApproved,
      field: 'actualDate',
    }),
  },
  {
    title: 'SS010 Invoice Number',
    key: 'SS010InvoiceNumber',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.candidateApproved,
      field: 'invoiceNumber',
    }),
  },
  {
    title: 'SS010 Invoice Date',
    key: 'SS010InvoiceDate',
    dataIndex: 'projectPaceTaskData',
    width: 170,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.candidateApproved,
      field: 'invoiceDate',
    }),
  },
  {
    title: 'RE066 Actual',
    key: 'RE066',
    dataIndex: 'projectPaceTaskData',
    width: 140,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.zoningAppDeemedComplete,
      field: 'actualDate',
    }),
  },
  {
    title: 'RE066 Invoice Number',
    key: 'RE066InvoiceNumber',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.zoningAppDeemedComplete,
      field: 'invoiceNumber',
    }),
  },
  {
    title: 'RE066 Invoice Date',
    key: 'RE066InvoiceDate',
    dataIndex: 'projectPaceTaskData',
    width: 170,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.zoningAppDeemedComplete,
      field: 'invoiceDate',
    }),
  },
  {
    title: 'RE015 Actual Date',
    key: 'RE015ActualDate',
    dataIndex: 'projectPaceTaskData',
    width: 160,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.leasingComplete,
      field: 'actualDate',
    }),
  },
  {
    title: 'RE015 OOM Amount',
    key: 'RE015OOMAmount',
    dataIndex: 'OOMInformationData',
    width: 190,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currenValue = value[MAIN_PACE_TASK_IDs.leasingComplete]?.OOMAmount;

      return currenValue ? `$ ${priceCommaReplacer(currenValue)}` : currenValue;
    },
  },
  {
    title: 'RE015 OOM Date',
    key: 'RE015OOMDate',
    dataIndex: 'OOMInformationData',
    width: 190,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currentValue = value[MAIN_PACE_TASK_IDs.leasingComplete]?.OOMDate;

      return currentValue
        ? readOnlyDatePickerRenderer(currentValue)
        : currentValue;
    },
  },
  {
    title: 'RE015 OOM Total Amount',
    key: 'RE015OOMTotalAmount',
    dataIndex: 'OOMInformationData',
    width: 210,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currenValue =
        value[MAIN_PACE_TASK_IDs.leasingComplete]?.OOMTotalAmount;

      return currenValue ? `$ ${priceCommaReplacer(currenValue)}` : currenValue;
    },
  },
  {
    title: 'RE015 Invoice Number',
    key: 'RE015InvoiceNumber',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.leasingComplete,
      field: 'invoiceNumber',
    }),
  },
  {
    title: 'RE015 Invoice Date',
    key: 'RE015InvoiceDate',
    dataIndex: 'projectPaceTaskData',
    width: 170,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.leasingComplete,
      field: 'invoiceDate',
    }),
  },
  {
    title: 'RE020 Actual Date',
    key: 'RE020SiteAcqCompleteActual',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.siteAcqComplete,
      field: 'actualDate',
    }),
  },
  {
    title: 'RE020 OOM Amount',
    key: 'RE020OOMAmount',
    dataIndex: 'OOMInformationData',
    width: 190,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currenValue = value[MAIN_PACE_TASK_IDs.siteAcqComplete]?.OOMAmount;

      return currenValue ? `$ ${priceCommaReplacer(currenValue)}` : currenValue;
    },
  },
  {
    title: 'RE020 OOM Date',
    key: 'RE020OOMDate',
    dataIndex: 'OOMInformationData',
    width: 190,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currentValue = value[MAIN_PACE_TASK_IDs.siteAcqComplete]?.OOMDate;

      return currentValue
        ? readOnlyDatePickerRenderer(currentValue)
        : currentValue;
    },
  },
  {
    title: 'RE020 OOM Total Amount',
    key: 'RE020OOMTotalAmount',
    dataIndex: 'OOMInformationData',
    width: 210,
    render: (value: Record<string, OOMInformation>): ReactNode => {
      const currenValue =
        value[MAIN_PACE_TASK_IDs.siteAcqComplete]?.OOMTotalAmount;

      return currenValue ? `$ ${priceCommaReplacer(currenValue)}` : currenValue;
    },
  },
  {
    title: 'RE020 Invoice Number',
    key: 'siteAcqCompleteRE020InvoiceNumber',
    dataIndex: 'projectPaceTaskData',
    width: 190,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.siteAcqComplete,
      field: 'invoiceNumber',
    }),
  },
  {
    title: 'RE020 Invoice Date',
    key: 'siteAcqCompleteRE020InvoiceDate',
    dataIndex: 'projectPaceTaskData',
    width: 200,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.siteAcqComplete,
      field: 'invoiceDate',
    }),
  },
  {
    title: 'RE097 PACE Actual Date',
    key: 'RE097',
    dataIndex: 'projectPaceTaskData',
    width: 200,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.OOMTruedUp,
      field: 'PACEActualDate',
    }),
  },
  {
    title: 'RE060 PACE Actual Date',
    key: 'RE060',
    dataIndex: 'projectPaceTaskData',
    width: 200,
    render: projectPaceTaskDataRender({
      id: MAIN_PACE_TASK_IDs.finalSiteAcqAccp,
      field: 'PACEActualDate',
    }),
  },
  {
    title: 'MDG Location Code',
    key: 'MDGLocationCode',
    dataIndex: 'site',
    width: 180,
    render: (site: SiteBrowse): ReactNode => site?.MDGLocationCode,
  },
  { ...actionButtons({ editFunc }), fixed: 'right' } as ColumnType<Browse>,
];
